import { FileText, ExternalLink, AlertCircle } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import type { CorpusFile } from '@/types';

interface CorpusItemProps {
  file: CorpusFile;
  disabled?: boolean;
}

export function CorpusItem({ file, disabled = false }: CorpusItemProps) {
  const hasValidUrl = file.presignedUrl && file.presignedUrl.trim() !== '';
  const isClickable = hasValidUrl && !disabled;



  const cardContent = (
    <CardContent className="p-3">
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-start gap-2 mb-1">
            <FileText className="h-3 w-3 text-muted-foreground flex-shrink-0 mt-0.5" />
            <h4 className="text-sm font-medium break-words leading-tight" title={file.displayName}>
              {file.displayName}
            </h4>
          </div>

          <div className="text-xs text-muted-foreground ml-5">
            {hasValidUrl && !disabled ? (
              <span>Available</span>
            ) : disabled ? (
              <span>Refreshing...</span>
            ) : (
              <span>File unavailable</span>
            )}
          </div>
        </div>

        <div className="flex-shrink-0 ml-2">
          {disabled ? (
            <div className="h-3 w-3 animate-spin rounded-full border border-muted border-t-primary" />
          ) : isClickable ? (
            <ExternalLink className="h-3 w-3 text-muted-foreground opacity-70 hover:opacity-100 transition-opacity" />
          ) : (
            <AlertCircle className="h-3 w-3 text-muted-foreground" />
          )}
        </div>
      </div>
    </CardContent>
  );

  if (isClickable) {
    return (
      <Card className="transition-colors hover:bg-muted/50">
        <a
          href={file.presignedUrl!}
          target="_blank"
          rel="noopener noreferrer"
          className="block cursor-pointer"
          aria-label={`Open ${file.displayName} in new tab`}
        >
          {cardContent}
        </a>
      </Card>
    );
  }

  return (
    <Card
      className={`transition-colors ${disabled
        ? 'opacity-40 cursor-wait'
        : 'opacity-60 cursor-not-allowed'
        }`}
      role="listitem"
      aria-label={
        disabled
          ? `${file.displayName} (loading...)`
          : `${file.displayName} (unavailable)`
      }
    >
      {cardContent}
    </Card>
  );
}